import React, { useState, useMemo, Suspense } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  Tooltip,
  ListItemButton,
  TextField,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import {
  Menu as MenuIcon,
  Calculate as CalculateIcon,
  Code as CodeIcon,
  Build as BuildIcon,
  Settings as SettingsIcon,
  Home as HomeIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  Celebration as PartyIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  FormatPaint as FormatPaintIcon,
  BugReport as BugReportIcon,
  EmojiEmotions as EmojiEmotionsIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import {
  EthUnitConversion,
  TokenUnitConversion,
  UnixEpochConversion,
  BaseConversion,
  StringBytes32Conversion,
  CalldataDecoder,
  EventDecoder,
  ConstructorEncoder,
  VanityAddressGenerator,
  UuidGenerator,
  JwtDecoder,
  JsonFormatter,
  HashGenerator,
  RandomTextGenerator,
  UrlEncoder,
  QrCodeGenerator,
  RandomDataGenerator,
  MinifierBeautifier,
  RegexTester,
  HttpRequestBuilder,
  ColorPicker,
  ClipboardInspector,
  EmojiPicker,
  TransactionDecoder,
  EthereumBlockTimeConverter,
  GasPriceTracker,
  ContractSecurityScanner,
  EnsLookup,
  AddressValidator,
  BalanceChecker,
  AbiEncoderDecoder,
  ContractVerificationHelper,
  ContractReadWriteUI,
  MevBotBuilder,
  HomePage,
  NotFoundPage,
} from './lazyComponents';
import NavbarPriceDisplay from './components/NavbarPriceDisplay';
import { useThemeContext } from './contexts/ThemeContext';
import CookieConsent from './components/CookieConsent';
import SEORoute from './components/SEORoute';
import CommandPalette from './components/CommandPalette';
import { useFavorites } from './contexts/FavoritesContext';
import { useRecentTools } from './hooks/useRecentTools';
import { toolCategories } from './data/toolsData';

import './App.css';

// Custom X (Twitter) Icon Component
const XIcon: React.FC = () => {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
  );
};

function AnimatedLogo() {
  return (
    <span className="bytescan-logo" title="ByteScan: Scan bytes, scan life!">
      <span role="img" aria-label="byte" style={{ marginRight: 8 }}>🦾</span> ByteScan
    </span>
  );
}

function ConfettiRain({ active }: { active: boolean }) {
  const confettiRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!active || !confettiRef.current) return;
    const colors = ['#00d4ff', '#a855f7', '#3fb950', '#f0883e', '#f85149', '#58a6ff', '#f472b6', '#fbbf24'];
    const confettiCount = 60;
    const container = confettiRef.current;
    container.innerHTML = '';
    for (let i = 0; i < confettiCount; i++) {
      const el = document.createElement('div');
      el.className = 'confetti-piece';
      el.style.left = Math.random() * 100 + 'vw';
      el.style.animationDelay = (Math.random() * 3) + 's';
      el.style.background = colors[Math.floor(Math.random() * colors.length)];
      el.style.opacity = String(0.7 + Math.random() * 0.3);
      el.style.width = 8 + Math.random() * 8 + 'px';
      el.style.height = 8 + Math.random() * 16 + 'px';
      el.style.borderRadius = Math.random() > 0.5 ? '50%' : '2px';
      container.appendChild(el);
    }
    return () => { container.innerHTML = ''; };
  }, [active]);
  return <div ref={confettiRef} className="confetti-rain" aria-hidden />;
}

// Icon mapping for categories
const categoryIcons: Record<string, React.ReactElement> = {
  AccountBalanceWallet: <AccountBalanceWalletIcon />,
  Code: <CodeIcon />,
  Calculate: <CalculateIcon />,
  Build: <BuildIcon />,
  Settings: <SettingsIcon />,
  FormatPaint: <FormatPaintIcon />,
  BugReport: <BugReportIcon />,
  EmojiEmotions: <EmojiEmotionsIcon />,
  TrendingUp: <TrendingUpIcon />,
  Security: <SecurityIcon />,
};

// Component mapping for routes
const componentMap: Record<string, React.ReactElement> = {
  '/ens-lookup': <EnsLookup />,
  '/address-validator': <AddressValidator />,
  '/balance-checker': <BalanceChecker />,
  '/abi-encoder-decoder': <AbiEncoderDecoder />,
  '/contract-verification-helper': <ContractVerificationHelper />,
  '/contract-read-write': <ContractReadWriteUI />,
  '/eth-unit-converter': <EthUnitConversion />,
  '/token-conversion': <TokenUnitConversion />,
  '/epoch-conversion': <UnixEpochConversion />,
  '/base-conversion': <BaseConversion />,
  '/string-bytes32': <StringBytes32Conversion />,
  '/url-encoder': <UrlEncoder />,
  '/eth-block-time': <EthereumBlockTimeConverter />,
  '/calldata-decoder': <CalldataDecoder />,
  '/event-decoder': <EventDecoder />,
  '/jwt-decoder': <JwtDecoder />,
  '/transaction-decoder': <TransactionDecoder />,
  '/constructor-encoder': <ConstructorEncoder />,
  '/vanity-address': <VanityAddressGenerator />,
  '/uuid-generator': <UuidGenerator />,
  '/hash-generator': <HashGenerator />,
  '/qr-generator': <QrCodeGenerator />,
  '/random-generator': <RandomDataGenerator />,
  '/random-text-generator': <RandomTextGenerator />,
  '/json-formatter': <JsonFormatter />,
  '/minifier': <MinifierBeautifier />,
  '/regex-tester': <RegexTester />,
  '/http-builder': <HttpRequestBuilder />,
  '/color-picker': <ColorPicker />,
  '/clipboard-inspector': <ClipboardInspector />,
  '/emoji-picker': <EmojiPicker />,
  '/gas-price-tracker': <GasPriceTracker />,
  '/mev-bot-builder': <MevBotBuilder />,
  '/contract-security-scanner': <ContractSecurityScanner />,
};

function AppContent() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [paletteOpen, setPaletteOpen] = useState(false);
  const location = useLocation();
  const { isDarkMode, toggleTheme, isPartyMode, togglePartyMode } = useThemeContext();
  const { toggleFavorite, isFavorite } = useFavorites();
  const recentPaths = useRecentTools();

  // Build menu items from shared data
  const menuItems = useMemo(() =>
    toolCategories.map((cat) => ({
      text: cat.text,
      icon: categoryIcons[cat.iconName] || <BuildIcon />,
      items: cat.items.map((item) => ({
        name: item.name,
        path: item.path,
      })),
    })),
  []);

  const allRoutes = useMemo(() => menuItems.flatMap((s) => s.items), [menuItems]);

  const [expandedSections, setExpandedSections] = useState(() =>
    new Set([...menuItems.map(s => s.text), 'Favorites', 'Recent'])
  );

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  React.useEffect(() => {
    if (isPartyMode) {
      document.body.classList.add('party-mode');
    } else {
      document.body.classList.remove('party-mode');
    }
    return () => document.body.classList.remove('party-mode');
  }, [isPartyMode]);

  React.useEffect(() => {
    if (isMobile) {
      setDrawerOpen(false);
    }
  }, [isMobile]);

  // Keyboard shortcuts
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        setPaletteOpen(true);
      }
      if (event.key === 'Escape' && searchQuery) {
        setSearchQuery('');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [searchQuery]);

  // Filter menu items based on search query
  const filteredMenuItems = searchQuery.trim() === '' ? menuItems : menuItems.map(section => ({
    ...section,
    items: section.items.filter(item =>
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      section.text.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(section => section.items.length > 0);

  const searchResults = searchQuery.trim() === '' ? [] : allRoutes.filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Favorite items
  const favoriteItems = allRoutes.filter((r) => isFavorite(r.path));

  // Recent items (exclude favorites to reduce clutter)
  const recentItems = recentPaths
    .map((path) => allRoutes.find((r) => r.path === path))
    .filter((r): r is { name: string; path: string } => r !== undefined)
    .filter((r) => !isFavorite(r.path))
    .slice(0, 5);

  const renderSidebarItem = (item: { name: string; path: string }, showStar: boolean = true) => (
    <ListItem
      key={item.path}
      sx={{
        pl: 3,
        pr: 1,
        backgroundColor: location.pathname === item.path
          ? (isDarkMode ? 'rgba(0, 212, 255, 0.08)' : 'rgba(0, 212, 255, 0.06)')
          : 'transparent',
        borderRadius: 2,
        mb: 0.5,
        mx: 1,
        transition: 'background-color 0.15s ease',
        '&:hover': {
          backgroundColor: isDarkMode ? 'rgba(0, 212, 255, 0.05)' : 'rgba(0, 212, 255, 0.04)',
        },
        ...(location.pathname === item.path && {
          borderLeft: '2px solid',
          borderColor: 'primary.main',
        }),
      }}
      onClick={() => isMobile && setDrawerOpen(false)}
      secondaryAction={
        showStar && drawerOpen ? (
          <IconButton
            edge="end"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              toggleFavorite(item.path);
            }}
            sx={{
              opacity: isFavorite(item.path) ? 1 : 0,
              transition: 'opacity 0.15s',
              '.MuiListItem-root:hover &': { opacity: isFavorite(item.path) ? 1 : 0.4 },
            }}
          >
            {isFavorite(item.path) ? (
              <StarIcon fontSize="small" sx={{ color: '#f0883e' }} />
            ) : (
              <StarBorderIcon fontSize="small" />
            )}
          </IconButton>
        ) : undefined
      }
    >
      <Link
        to={item.path}
        style={{ textDecoration: 'none', color: 'inherit', width: '100%' }}
      >
        <ListItemText
          primary={item.name}
          primaryTypographyProps={{
            sx: {
              fontSize: '0.85rem',
              fontWeight: location.pathname === item.path ? 600 : 400,
              color: location.pathname === item.path ? 'primary.main' : 'text.secondary',
            }
          }}
        />
      </Link>
    </ListItem>
  );

  const drawerWidth = 280;
  const collapsedWidth = 64;

  return (
    <>
      <ConfettiRain active={isPartyMode} />
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        {/* Top Navigation Bar */}
        <AppBar position="static" elevation={0}>
          <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
            <Tooltip title={isMobile ? "Open menu" : (drawerOpen ? "Hide sidebar" : "Show sidebar")} arrow>
              <IconButton
                size="large"
                edge="start"
                color="inherit"
                aria-label="menu"
                sx={{ mr: 1.5 }}
                onClick={() => setDrawerOpen(!drawerOpen)}
              >
                <MenuIcon />
              </IconButton>
            </Tooltip>
            <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
              <AnimatedLogo />
            </Link>
            <Box sx={{ flexGrow: 1 }} />
            {/* Desktop price display */}
            <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
              <NavbarPriceDisplay />
            </Box>
            <Tooltip title="Follow us on X" arrow>
              <IconButton
                color="inherit"
                component="a"
                href="https://x.com/Sebear_tech"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  ml: 0.5,
                  opacity: 0.7,
                  '&:hover': { opacity: 1 },
                }}
              >
                <XIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={isPartyMode ? 'Turn off Party Mode' : 'Turn on Party Mode!'} arrow>
              <IconButton
                onClick={togglePartyMode}
                sx={{
                  ml: 0.5,
                  color: isPartyMode ? 'secondary.main' : 'inherit',
                  opacity: isPartyMode ? 1 : 0.7,
                  '&:hover': { opacity: 1 },
                }}
              >
                <PartyIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'} arrow>
              <IconButton
                color="inherit"
                onClick={toggleTheme}
                sx={{
                  ml: 0.5,
                  opacity: 0.7,
                  '&:hover': { opacity: 1 },
                }}
              >
                {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Tooltip>
          </Toolbar>
        </AppBar>

        {/* Mobile price bar */}
        <Box sx={{
          display: { xs: 'flex', md: 'none' },
          bgcolor: isDarkMode ? 'rgba(13, 17, 23, 0.8)' : 'background.paper',
          backdropFilter: 'blur(12px)',
          borderBottom: 1,
          borderColor: 'divider',
          px: 2,
          py: 1,
          overflow: 'auto',
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          minHeight: '40px',
          alignItems: 'center'
        }}>
          <NavbarPriceDisplay />
        </Box>

        <Box sx={{ display: 'flex', flexGrow: 1 }}>

        <Drawer
          anchor="left"
          open={isMobile ? drawerOpen : true}
          variant={isMobile ? "temporary" : "persistent"}
          onClose={() => setDrawerOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: isMobile ? '100%' : (drawerOpen ? drawerWidth : collapsedWidth),
              maxWidth: isMobile ? '100%' : (drawerOpen ? drawerWidth : collapsedWidth),
              transition: 'width 0.3s ease-in-out',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          <Box
            component="nav"
            aria-label="Tool navigation"
            sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}
            role="presentation"
            onClick={!isMobile && !drawerOpen ? () => setDrawerOpen(true) : undefined}
            style={!isMobile && !drawerOpen ? { cursor: 'pointer' } : undefined}
          >
            <List sx={{ flexGrow: 1, overflow: 'auto', py: 1 }}>
              <ListItem sx={{ minHeight: 56, justifyContent: 'center', px: 2 }}>
                {drawerOpen ? (
                  <>
                    <ListItemIcon sx={{ minWidth: 36, color: 'primary.main' }}>
                      <HomeIcon />
                    </ListItemIcon>
                    <ListItemText primary={
                      <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
                        <AnimatedLogo />
                      </Link>
                    } />
                  </>
                ) : (
                  <Tooltip title="ByteScan" arrow placement="right">
                    <ListItemIcon sx={{ justifyContent: 'center', color: 'primary.main' }}>
                      <HomeIcon />
                    </ListItemIcon>
                  </Tooltip>
                )}
              </ListItem>
              <Divider sx={{ mx: 2, my: 0.5 }} />

              {/* Search Bar */}
              {drawerOpen && (
                <>
                  <ListItem sx={{ px: 2, py: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search tools... (Ctrl+K)"
                      aria-label="Search tools"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                        endAdornment: searchQuery && (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setSearchQuery('')}
                              edge="end"
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </ListItem>
                  <Divider sx={{ mx: 2, my: 0.5 }} />
                </>
              )}

              {/* Favorites Section */}
              {drawerOpen && favoriteItems.length > 0 && searchQuery.trim() === '' && (
                <>
                  <ListItemButton onClick={() => toggleSection('Favorites')} sx={{ mx: 1, borderRadius: 2 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <StarIcon sx={{ color: '#f0883e', fontSize: '1.2rem' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Favorites"
                      primaryTypographyProps={{ sx: { fontSize: '0.85rem', fontWeight: 600 } }}
                    />
                    {expandedSections.has('Favorites') ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </ListItemButton>
                  {expandedSections.has('Favorites') &&
                    favoriteItems.map((item) => renderSidebarItem(item, true))
                  }
                  <Divider sx={{ mx: 2, my: 0.5 }} />
                </>
              )}

              {/* Recent Section */}
              {drawerOpen && recentItems.length > 0 && searchQuery.trim() === '' && (
                <>
                  <ListItemButton onClick={() => toggleSection('Recent')} sx={{ mx: 1, borderRadius: 2 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <HistoryIcon sx={{ fontSize: '1.2rem', color: 'text.secondary' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Recent"
                      primaryTypographyProps={{ sx: { fontSize: '0.85rem', fontWeight: 600 } }}
                    />
                    {expandedSections.has('Recent') ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </ListItemButton>
                  {expandedSections.has('Recent') &&
                    recentItems.map((item) => renderSidebarItem(item, false))
                  }
                  <Divider sx={{ mx: 2, my: 0.5 }} />
                </>
              )}

              {/* Show search results or filtered menu items */}
              {searchQuery.trim() !== '' ? (
                <>
                  <ListItem sx={{ px: 2, py: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                      {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for &ldquo;{searchQuery}&rdquo;
                    </Typography>
                  </ListItem>
                  {searchResults.map((item) => (
                    <ListItem
                      key={item.name}
                      sx={{
                        pl: 2,
                        backgroundColor: location.pathname === item.path
                          ? (isDarkMode ? 'rgba(0, 212, 255, 0.08)' : 'rgba(0, 212, 255, 0.06)')
                          : 'transparent',
                        borderRadius: 2,
                        mb: 0.5,
                        mx: 1
                      }}
                      onClick={() => {
                        setSearchQuery('');
                        isMobile && setDrawerOpen(false);
                      }}
                    >
                      <Tooltip title={`Go to ${item.name}`} arrow placement="right">
                        <Link
                          to={item.path}
                          style={{ textDecoration: 'none', color: 'inherit', width: '100%', display: 'flex', alignItems: 'center' }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {menuItems.find(section =>
                              section.items.some(sectionItem => sectionItem.name === item.name)
                            )?.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={item.name}
                            primaryTypographyProps={{
                              sx: { fontSize: '0.85rem' }
                            }}
                          />
                        </Link>
                      </Tooltip>
                    </ListItem>
                  ))}
                  {searchResults.length === 0 && (
                    <ListItem sx={{ px: 2, py: 2 }}>
                      <Typography variant="body2" color="text.secondary" align="center" sx={{ width: '100%' }}>
                        No tools found
                      </Typography>
                    </ListItem>
                  )}
                </>
              ) : (
                filteredMenuItems.map((section) => (
                  <React.Fragment key={section.text}>
                    <ListItemButton
                      onClick={() => toggleSection(section.text)}
                      sx={{ cursor: 'pointer', userSelect: 'none', mx: 1, borderRadius: 2 }}
                    >
                      <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>
                        {section.icon}
                      </ListItemIcon>
                      {drawerOpen && (
                        <ListItemText
                          primary={section.text}
                          primaryTypographyProps={{ sx: { fontSize: '0.85rem', fontWeight: 600 } }}
                        />
                      )}
                      {drawerOpen && (expandedSections.has(section.text) ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />)}
                    </ListItemButton>
                    {drawerOpen && expandedSections.has(section.text) && section.items.map((item) =>
                      renderSidebarItem(item)
                    )}
                    {drawerOpen && <Divider sx={{ mx: 2, my: 0.5 }} />}
                  </React.Fragment>
                ))
              )}
            </List>

            {/* Collapse/Expand Button */}
            {!isMobile && (
              <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider' }}>
                <Tooltip title={drawerOpen ? "Collapse sidebar" : "Expand sidebar"} arrow placement="right">
                  <IconButton
                    onClick={() => setDrawerOpen(!drawerOpen)}
                    sx={{
                      width: '100%',
                      borderRadius: 2,
                      color: 'text.secondary',
                      '&:hover': {
                        backgroundColor: isDarkMode ? 'rgba(0, 212, 255, 0.05)' : 'rgba(0, 212, 255, 0.04)',
                      }
                    }}
                  >
                    {drawerOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        </Drawer>

        <Box
          component="main"
          role="main"
          sx={{
            flexGrow: 1,
            ml: isMobile ? 0 : (drawerOpen ? `${drawerWidth}px` : `${collapsedWidth}px`),
            transition: 'margin-left 0.3s ease-in-out',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <Suspense fallback={
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                <CircularProgress size={32} sx={{ color: 'primary.main' }} />
              </Box>
            }>
              <Routes>
                <Route path="/" element={<HomePage />} />
                {allRoutes.map((route) => {
                  const component = componentMap[route.path];
                  if (!component) return null;
                  return (
                    <Route
                      key={route.path}
                      path={route.path}
                      element={
                        <SEORoute path={route.path}>
                          {component}
                        </SEORoute>
                      }
                    />
                  );
                })}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </Box>
        </Box>
        </Box>
      </Box>

      {/* Cookie Consent Banner */}
      <CookieConsent />
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
